import { useRef, useEffect, useCallback, useState } from "react";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { $staffApi } from "../api";
import { fetchWithRetry } from "../utilities/api";

export type UploadedFile = {
  name: string;
  url: string;
  isLoading: boolean;
  previewUrl: string;
  signature: string;
};

export function useFileUpload(): {
  files: UploadedFile[];
  isUploading: boolean;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (file: UploadedFile) => void;
  reset: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
} {
  const { sendNotification } = useNotificationContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set());

  const [files, setFiles] = useState<UploadedFile[]>([]);

  // Note: This is hardcoded to hit the policy endpoint for "messages" file uploads.
  // If we need to reuse this hook for other upload types, we can parameterize this.
  const { mutateAsync: getUploadPolicy } = $staffApi.useMutation("post", "/api/messages/upload/");

  const removeFileFromInput = useCallback((file: UploadedFile | File) => {
    if ("previewUrl" in file) {
      URL.revokeObjectURL(file.previewUrl);
      blobUrlsRef.current.delete(file.previewUrl);
    }
    if (fileInputRef.current?.files) {
      const updatedFiles = Array.from(fileInputRef.current.files).filter(
        (f) => f.name !== file.name
      );
      const dataTransfer = new DataTransfer();
      updatedFiles.forEach((f) => dataTransfer.items.add(f));
      fileInputRef.current.files = dataTransfer.files;
    }
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const filesArray = Array.from(e.target.files ?? []);
      if (!filesArray.length) return;

      filesArray.forEach((file) => {
        (async () => {
          try {
            const policyData = await getUploadPolicy({
              body: {
                name: file.name,
                type: file.type,
              },
            });
            const previewUrl = URL.createObjectURL(file);
            blobUrlsRef.current.add(previewUrl);
            setFiles((files) =>
              files
                .filter((f) => f.name !== file.name)
                .concat({
                  name: file.name,
                  url: "",
                  isLoading: true,
                  previewUrl,
                  signature: policyData.signature,
                })
            );

            const formData = new FormData();
            Object.entries(policyData).forEach(([key, value]) => {
              if (key !== "form_action" && key !== "signature") {
                formData.append(key, value as string);
              }
            });
            formData.append("file", file);
            const response = await fetchWithRetry(
              policyData.form_action,
              {
                method: "POST",
                body: formData,
              },
              { errorMessage: "File upload failed after retries" }
            );
            if (!response.ok) {
              throw new Error("File upload failed");
            }

            const xmlText = await response.text();
            const fileUrl = getUrlFromS3XMLResponse(xmlText);

            setFiles((files) =>
              files.map((f) =>
                f.name === file.name ? { ...f, url: fileUrl, isLoading: false } : f
              )
            );
          } catch (err) {
            setFiles((files) => files.filter((f) => f.name !== file.name));
            removeFileFromInput(file);
            sendNotification({
              type: "negative",
              text: `Failed to upload ${file.name}`,
            });
          }
        })();
      });
    },
    [getUploadPolicy, removeFileFromInput, sendNotification]
  );

  const removeFile = useCallback(
    (file: UploadedFile) => {
      setFiles((files) => files.filter((f) => f.name !== file.name));
      removeFileFromInput(file);
    },
    [removeFileFromInput]
  );

  const reset = useCallback(() => {
    blobUrlsRef.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    blobUrlsRef.current.clear();
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Cleanup all blob URLs when component unmounts
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrls.clear();
    };
  }, []);

  return {
    files,
    isUploading: files.some((file) => file.isLoading),
    handleFileChange,
    removeFile,
    reset,
    fileInputRef,
  };
}

/**
 * Extracts the file URL from S3's XML response after upload.
 * S3 returns the URL in a <Location> tag, URL-encoded.
 * This function decodes it to get the actual file URL.
 */
export function getUrlFromS3XMLResponse(xmlText: string): string {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "text/xml");
  const locationTag = xmlDoc.getElementsByTagName("Location")[0];
  if (!locationTag || !locationTag.childNodes[0]) {
    throw new Error("Failed to extract URL from S3 response");
  }
  const encodedUrl = locationTag.childNodes[0].nodeValue;
  if (!encodedUrl) {
    throw new Error("Failed to extract URL from S3 response");
  }
  return decodeURIComponent(encodedUrl);
}
